<script setup lang="ts">
import { computed, inject, type Ref } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { servicePricesApi } from '@/api/service-prices';
import { formatCurrency } from '@/utils/currency';
import { type SerializedUser } from '@/api/users';
import { usePermissions } from '@/composables/usePermissions';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import PatientServicePriceForm from './patient-service-price-form.vue';

const props = defineProps<{
  patientId: number;
}>();

const { tableProps } = useResponsiveTable();

const { hasPermission } = usePermissions();

const currentUser = inject<Ref<SerializedUser>>('currentUser');

const bonusFeatureEnabled = computed(() =>
  currentUser?.value?.companyBonusFeatureEnabled ?? false,
);

const { data: servicePrices, isLoading } = useQuery({
  queryKey: ['servicePrices'],
  queryFn: () => servicePricesApi.index(props.patientId),
  initialData: [],
});

const hasActions = computed(() =>
  hasPermission.value('service_prices', 'update'),
);

const tableHeaders = computed(() => {
  const baseHeaders: { title: string, key: string, sortable?: boolean }[] = [
    { title: 'Servicio', key: 'humanServiceType' },
    { title: 'Precio Semana', key: 'weekPrice' },
    { title: 'Pago Semana (líquido)', key: 'weekCost' },
    { title: 'Precio Fin de Semana', key: 'weekendPrice' },
    { title: 'Pago Fin de Semana (líquido)', key: 'weekendCost' },
  ];

  if (bonusFeatureEnabled.value) {
    baseHeaders.push(
      { title: 'Bonificación', key: 'bonus' },
      { title: 'Bono con boleta', key: 'bonusWithTaxBill' },
    );
  }
  baseHeaders.push(
    { title: 'Con boleta de honorarios', key: 'withTaxBill' },
  );

  if (hasActions.value) {
    baseHeaders.push({ title: 'Acciones', key: 'actions', sortable: false });
  }

  return baseHeaders;
});
</script>

<template>
  <div>
    <div class="d-flex justify-space-between align-center mb-4">
      <span class="text-h6">Precios de Servicios del Paciente</span>
      <v-dialog
        max-width="800"
        scrollable
      >
        <template #activator="{ props: activatorProps }">
          <v-btn
            v-if="hasPermission('service_prices', 'create')"
            color="primary"
            v-bind="activatorProps"
          >
            Agregar
          </v-btn>
        </template>
        <template #default="{ isActive }">
          <patient-service-price-form
            :patient-id="props.patientId"
            @close="isActive.value = false"
          />
        </template>
      </v-dialog>
    </div>

    <v-data-table
      v-bind="tableProps"
      :headers="tableHeaders"
      :items="servicePrices"
      :loading="isLoading"
      loading-text="Cargando los precios de servicios del paciente..."
      no-data-text="No hay precios de servicios del paciente"
    >
      <template #[`item.weekPrice`]="{ item }">
        {{ formatCurrency(item.weekPrice) }}
      </template>
      <template #[`item.weekCost`]="{ item }">
        {{ formatCurrency(item.weekCost) }}
      </template>
      <template #[`item.weekendPrice`]="{ item }">
        {{ formatCurrency(item.weekendPrice) }}
      </template>
      <template #[`item.weekendCost`]="{ item }">
        {{ formatCurrency(item.weekendCost) }}
      </template>
      <template
        v-if="bonusFeatureEnabled"
        #[`item.bonus`]="{ item }"
      >
        <span v-if="item.bonus">{{ formatCurrency(item.bonus) }}</span>
        <span
          v-else
          class="text-grey"
        >
          —
        </span>
      </template>
      <template
        v-if="bonusFeatureEnabled"
        #[`item.bonusWithTaxBill`]="{ item }"
      >
        <span v-if="item.bonus">{{ item.bonusWithTaxBill ? 'Sí' : 'No' }}</span>
        <span
          v-else
          class="text-grey"
        >
          —
        </span>
      </template>
      <template #[`item.withTaxBill`]="{ item }">
        <v-chip :color="item.withTaxBill ? 'green' : 'red'">
          {{ item.withTaxBill ? 'Sí' : 'No' }}
        </v-chip>
      </template>
      <template #[`item.actions`]="{ item }">
        <v-dialog
          max-width="800"
          scrollable
        >
          <template #activator="{ props: activatorProps }">
            <v-btn
              v-if="hasPermission('service_prices', 'update')"
              v-bind="activatorProps"
              icon="mdi-pencil"
              size="small"
              class="me-2"
            />
          </template>
          <template #default="{ isActive }">
            <patient-service-price-form
              :patient-id="props.patientId"
              :service-price="item"
              @close="isActive.value = false"
            />
          </template>
        </v-dialog>
      </template>
    </v-data-table>
  </div>
</template>
